import React, { useState, useEffect, useCallback } from 'react';
import {
  Package,
  ChevronDown,
  ChevronUp,
  RefreshCw,
  ClipboardList,
  Pencil,
  Loader2
} from 'lucide-react';
import vendorApi from '../../services/vendorApi';
import { describeInventoryAudit } from './describeInventoryAudit';
import {
  ADMIN_ICON_STROKE,
  AdminSectionTitle,
  adminCardClass
} from '../admin/AdminShared';

/**
 * Stay-wise inventory calendar + per-room day edits + audit log.
 * Used by vendor and admin shells; `fetchStays` supplies the stay picker list.
 */
const StayInventoryDashboard = ({
  fetchStays,
  theme = 'vendor',
  showPageHeader = false
}) => {
  const isAdminTheme = theme === 'admin';

  const [stays, setStays] = useState([]);
  const [stayId, setStayId] = useState('');
  const [calendar, setCalendar] = useState(null);
  const [audit, setAudit] = useState([]);
  const [auditCursor, setAuditCursor] = useState(null);
  const [loadingStays, setLoadingStays] = useState(true);
  const [loadingCal, setLoadingCal] = useState(false);
  const [loadingAudit, setLoadingAudit] = useState(false);
  const [ensuring, setEnsuring] = useState(false);
  const [error, setError] = useState('');
  const [expandedRooms, setExpandedRooms] = useState({});
  const [editModal, setEditModal] = useState(null);
  const [editForm, setEditForm] = useState({
    totalCapacity: '',
    holdCount: '',
    deltaTotal: '',
    note: ''
  });
  const [savingEdit, setSavingEdit] = useState(false);

  const cardClass = isAdminTheme
    ? `${adminCardClass} overflow-hidden`
    : 'rounded-xl border border-gray-200 bg-white overflow-hidden';

  const selectClass = isAdminTheme
    ? 'w-full mb-4 px-4 py-2.5 border border-[#E5E3DF] rounded-[12px] bg-white text-gray-900 focus:ring-2 focus:ring-[#FF5E4C]/30 focus:border-[#FF5E4C]'
    : 'w-full mb-4 px-3 py-2 border border-gray-200 rounded-lg bg-white text-gray-900';

  const btnSecondaryClass = isAdminTheme
    ? 'flex-1 flex items-center justify-center gap-2 py-2.5 px-3 rounded-full border border-[#E5E3DF] bg-white text-black text-sm font-medium hover:bg-[#FAF7F2] disabled:opacity-60'
    : 'flex-1 flex items-center justify-center gap-2 py-2.5 px-3 rounded-lg border border-gray-200 bg-white text-gray-800 text-sm font-medium';

  const btnPrimaryClass = isAdminTheme
    ? 'flex-1 py-2.5 px-3 rounded-full bg-[#FF5E4C] text-white text-sm font-medium disabled:opacity-60 hover:opacity-90'
    : 'flex-1 py-2.5 px-3 rounded-lg bg-[#FF5E4C] text-white text-sm font-medium disabled:opacity-60';

  const loadStays = useCallback(async () => {
    try {
      setLoadingStays(true);
      setError('');
      const data = await fetchStays();
      const list = data.stays || [];
      setStays(list);
      setStayId((prev) =>
        prev && list.some((s) => s.stayId === prev) ? prev : list[0]?.stayId || ''
      );
    } catch (e) {
      setError(e.response?.data?.error || 'Could not load stays');
    } finally {
      setLoadingStays(false);
    }
  }, [fetchStays]);

  useEffect(() => {
    loadStays();
  }, [loadStays]);

  const loadCalendar = useCallback(async () => {
    if (!stayId) return;
    try {
      setLoadingCal(true);
      setError('');
      const data = await vendorApi.getInventoryCalendar(stayId, 30);
      setCalendar(data);
    } catch (e) {
      setError(e.response?.data?.error || 'Could not load calendar');
      setCalendar(null);
    } finally {
      setLoadingCal(false);
    }
  }, [stayId]);

  useEffect(() => {
    loadCalendar();
  }, [loadCalendar]);

  const loadAudit = useCallback(
    async (cursor = null, append = false) => {
      if (!stayId) return;
      try {
        setLoadingAudit(true);
        const data = await vendorApi.getInventoryAudit(stayId, 25, cursor);
        setAudit((prev) => (append ? [...prev, ...data.logs] : data.logs));
        setAuditCursor(data.nextCursor || null);
      } catch (e) {
        setError(e.response?.data?.error || 'Could not load audit log');
      } finally {
        setLoadingAudit(false);
      }
    },
    [stayId]
  );

  useEffect(() => {
    setAudit([]);
    setAuditCursor(null);
    if (stayId) loadAudit(null, false);
  }, [stayId, loadAudit]);

  const handleEnsureWindow = async () => {
    if (!stayId) return;
    try {
      setEnsuring(true);
      setError('');
      await vendorApi.ensureInventoryWindow(stayId, 30);
      await loadCalendar();
    } catch (e) {
      setError(e.response?.data?.error || 'Could not seed inventory window');
    } finally {
      setEnsuring(false);
    }
  };

  const openEdit = (roomId, date, dayRow) => {
    setEditModal({ roomId, date, dayRow });
    setEditForm({
      totalCapacity: String(dayRow.totalAvailableUnits ?? ''),
      holdCount: String(dayRow.holdUnits ?? '0'),
      deltaTotal: '',
      note: ''
    });
  };

  const submitEdit = async () => {
    if (!editModal || !stayId) return;
    const body = {};
    if (editForm.deltaTotal !== '' && editForm.deltaTotal != null) {
      const n = parseInt(editForm.deltaTotal, 10);
      if (!Number.isNaN(n)) body.deltaTotal = n;
    }
    if (editForm.totalCapacity !== '') {
      const n = parseInt(editForm.totalCapacity, 10);
      if (!Number.isNaN(n)) body.totalCapacity = n;
    }
    if (editForm.holdCount !== '') {
      const n = parseInt(editForm.holdCount, 10);
      if (!Number.isNaN(n)) body.holdCount = n;
    }
    if (editForm.note) body.note = editForm.note;
    if (!Object.keys(body).length) {
      setError('Set total capacity, hold count, and/or delta');
      return;
    }
    try {
      setSavingEdit(true);
      setError('');
      await vendorApi.patchInventoryDay(stayId, editModal.roomId, editModal.date, body);
      setEditModal(null);
      await loadCalendar();
      loadAudit(null, false);
    } catch (e) {
      setError(e.response?.data?.error || e.response?.data?.details || 'Update failed');
    } finally {
      setSavingEdit(false);
    }
  };

  const SectionHeading = ({ children, className = '' }) =>
    isAdminTheme ? (
      <AdminSectionTitle className={`mb-2 ${className}`}>{children}</AdminSectionTitle>
    ) : (
      <h2 className={`text-sm font-semibold text-gray-800 mb-2 ${className}`}>{children}</h2>
    );

  return (
    <div>
      {showPageHeader && (
        <div className="flex items-center gap-3 mb-6">
          <Package className="w-7 h-7 text-[#FF5E4C] shrink-0" />
          <div>
            <h1 className="text-[20px] md:text-[22px] font-medium text-black tracking-[-0.02em]">
              Inventory
            </h1>
            <p className="text-[14px] text-[#78736A] mt-1">30-day availability by room</p>
          </div>
        </div>
      )}

      {error && (
        <div
          className={`mb-4 p-3 rounded-[12px] text-sm ${
            isAdminTheme ? 'bg-red-50 text-red-800 border border-red-100' : 'bg-red-50 text-red-800'
          }`}
        >
          {error}
        </div>
      )}

      {loadingStays ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 text-[#FF5E4C] animate-spin" />
        </div>
      ) : stays.length === 0 ? (
        <p className={`text-center py-8 ${isAdminTheme ? 'text-[#78736A]' : 'text-gray-600'}`}>
          No stays found.
        </p>
      ) : (
        <>
          <label
            className={`block text-sm font-medium mb-1 ${
              isAdminTheme ? 'text-black' : 'text-gray-700'
            }`}
          >
            Stay
          </label>
          <select value={stayId} onChange={(e) => setStayId(e.target.value)} className={selectClass}>
            {stays.map((s) => (
              <option key={s.stayId} value={s.stayId}>
                {s.title || s.stayId}
                {s.city ? ` · ${s.city}` : ''}
              </option>
            ))}
          </select>

          <div className="flex gap-2 mb-4">
            <button
              type="button"
              onClick={() => loadCalendar()}
              disabled={loadingCal}
              className={btnSecondaryClass}
            >
              <RefreshCw className={`w-4 h-4 ${loadingCal ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            <button
              type="button"
              onClick={handleEnsureWindow}
              disabled={ensuring}
              className={btnPrimaryClass}
            >
              {ensuring ? 'Working…' : 'Ensure 30-day rows'}
            </button>
          </div>

          {calendar && (
            <p className={`mb-2 text-sm ${isAdminTheme ? 'text-[#78736A]' : 'text-gray-600'}`}>
              Bookable rooms:{' '}
              <span className="font-semibold text-gray-900">{calendar.roomCount}</span>
              <span className="text-gray-400 mx-1">·</span>
              1 unit / room / night
            </p>
          )}

          {loadingCal && !calendar ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-8 h-8 text-[#FF5E4C] animate-spin" />
            </div>
          ) : calendar ? (
            <>
              <SectionHeading className={isAdminTheme ? 'mt-6' : 'mt-6 flex items-center gap-2'}>
                Stay totals (all rooms)
              </SectionHeading>
              <div className={`${cardClass} mb-6`}>
                <div className="max-h-64 overflow-y-auto divide-y divide-[#E5E3DF]">
                  {calendar.stayDailyTotals.map((row) => (
                    <div
                      key={row.date}
                      className="px-4 py-2.5 flex justify-between text-xs sm:text-sm"
                    >
                      <span className="text-gray-700">{row.date}</span>
                      <span className="text-gray-900 tabular-nums">
                        <span className="text-gray-500">rem</span>{' '}
                        <strong>{row.remaining}</strong>
                        <span className="text-gray-400 mx-1">·</span>
                        <span className="text-gray-500">booked</span> {row.bookedUnits}
                        <span className="text-gray-400 mx-1">·</span>
                        <span className="text-gray-500">total</span> {row.totalAvailableUnits}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              <SectionHeading>By room type</SectionHeading>
              <div className="space-y-2 mb-6">
                {calendar.byRoom.map((room) => (
                  <div key={room.roomId} className={cardClass}>
                    <button
                      type="button"
                      onClick={() =>
                        setExpandedRooms((prev) => ({
                          ...prev,
                          [room.roomId]: !prev[room.roomId]
                        }))
                      }
                      className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-[#FAF7F2]/50 transition-colors"
                    >
                      <div>
                        <div className="font-medium text-gray-900">{room.title}</div>
                        <div className="text-xs text-[#78736A]">
                          Sleeps {room.guestCapacity ?? room.capacity} · 1 unit/night
                        </div>
                      </div>
                      {expandedRooms[room.roomId] ? (
                        <ChevronUp className="w-5 h-5 text-gray-400" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-gray-400" />
                      )}
                    </button>
                    {expandedRooms[room.roomId] && (
                      <div className="border-t border-[#E5E3DF] max-h-56 overflow-y-auto">
                        {room.days.map((d) => (
                          <div
                            key={d.date}
                            className="px-4 py-2 flex items-center justify-between text-xs border-b border-[#E5E3DF]/60 last:border-0"
                          >
                            <div>
                              <div className="text-gray-800">{d.date}</div>
                              <div className="text-[#78736A]">
                                rem {d.remaining} · booked {d.bookedUnits} · hold {d.holdUnits} ·
                                total {d.totalAvailableUnits}
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() => openEdit(room.roomId, d.date, d)}
                              className="p-2 rounded-full text-[#FF5E4C] hover:bg-[#FAF7F2]"
                              aria-label="Edit day"
                            >
                              <Pencil className="w-4 h-4" strokeWidth={ADMIN_ICON_STROKE} />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>

              <SectionHeading className="flex items-center gap-2">
                <ClipboardList className="w-4 h-4" strokeWidth={ADMIN_ICON_STROKE} />
                Change log
              </SectionHeading>
              <div className={`${cardClass} p-4 space-y-3 max-h-80 overflow-y-auto`}>
                {audit.length === 0 && !loadingAudit ? (
                  <p className="text-sm text-[#78736A]">No entries yet.</p>
                ) : (
                  audit.map((log) => {
                    const described = describeInventoryAudit(log);
                    return (
                      <div key={log.SK} className="text-xs border-b border-[#E5E3DF] pb-2 last:border-0">
                        <div className="font-medium text-gray-800">{described.title}</div>
                        <div className="text-[10px] text-gray-400 font-mono">{log.action}</div>
                        <div className="text-[#78736A]">
                          {log.createdAt}
                          {log.roomId ? ` · ${log.roomId}` : ''}
                          {log.date ? ` · ${log.date}` : ''}
                        </div>
                        {described.detail && (
                          <div className="text-gray-600 mt-1">{described.detail}</div>
                        )}
                      </div>
                    );
                  })
                )}
                {auditCursor && (
                  <button
                    type="button"
                    onClick={() => loadAudit(auditCursor, true)}
                    disabled={loadingAudit}
                    className="w-full py-2 text-sm text-[#FF5E4C] font-medium"
                  >
                    {loadingAudit ? 'Loading…' : 'Load more'}
                  </button>
                )}
              </div>
            </>
          ) : null}
        </>
      )}

      {editModal && (
        <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/40 p-4">
          <div
            className={`w-full max-w-md bg-white shadow-xl p-5 mb-safe ${
              isAdminTheme ? 'rounded-[16px] border border-[#E5E3DF]' : 'rounded-2xl'
            }`}
          >
            <h3 className="font-semibold text-gray-900 mb-1">Edit inventory</h3>
            <p className="text-sm text-[#78736A] mb-4">
              {editModal.date} · {editModal.roomId}
            </p>
            <label className="block text-xs text-gray-600 mb-1">Sellable units (0 or 1)</label>
            <input
              type="number"
              min={0}
              max={1}
              value={editForm.totalCapacity}
              onChange={(e) => setEditForm((f) => ({ ...f, totalCapacity: e.target.value }))}
              className="w-full mb-3 px-3 py-2 border border-[#E5E3DF] rounded-[12px]"
            />
            <label className="block text-xs text-gray-600 mb-1">Hold / blocked (0 or 1)</label>
            <input
              type="number"
              min={0}
              max={1}
              value={editForm.holdCount}
              onChange={(e) => setEditForm((f) => ({ ...f, holdCount: e.target.value }))}
              className="w-full mb-3 px-3 py-2 border border-[#E5E3DF] rounded-[12px]"
            />
            <label className="block text-xs text-gray-600 mb-1">Delta total (e.g. -1 or +2)</label>
            <input
              type="number"
              value={editForm.deltaTotal}
              onChange={(e) => setEditForm((f) => ({ ...f, deltaTotal: e.target.value }))}
              className="w-full mb-3 px-3 py-2 border border-[#E5E3DF] rounded-[12px]"
              placeholder="Optional if you set total above"
            />
            <label className="block text-xs text-gray-600 mb-1">Note (optional)</label>
            <input
              type="text"
              value={editForm.note}
              onChange={(e) => setEditForm((f) => ({ ...f, note: e.target.value }))}
              className="w-full mb-4 px-3 py-2 border border-[#E5E3DF] rounded-[12px]"
            />
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setEditModal(null)}
                className={
                  isAdminTheme
                    ? 'flex-1 py-2.5 rounded-full border border-[#E5E3DF] text-black'
                    : 'flex-1 py-2.5 rounded-lg border border-gray-200 text-gray-800'
                }
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={submitEdit}
                disabled={savingEdit}
                className={`flex-1 py-2.5 font-medium disabled:opacity-60 ${
                  isAdminTheme ? 'rounded-full bg-[#FF5E4C] text-white' : 'rounded-lg bg-[#FF5E4C] text-white'
                }`}
              >
                {savingEdit ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StayInventoryDashboard;
